import React, { Fragment, useCallback, useEffect, useState } from "react";
import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetFlatList,
  BottomSheetView,
} from "@gorhom/bottom-sheet";
import { Pressable, useWindowDimensions, View, Text } from "react-native";
import { useSheet } from "../shared/sheet-provider";
import CloseIcon from "@/assets/svgs/close-icon";
import { remapProps } from "react-native-css-interop";
import { SheetIds } from "../utils/sheet-ids";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import { RadioInput } from "../ui/radio-input";
import { Avatar } from "../ui/avatar";

type Option = {
  label: string;
  value: string;
  disabled?: boolean;
  imageUrl: string | null;
};

export type ChildrenSelectSheetPayload = {
  title: string;
  options: Option[];
  preSelectedOption?: string | number;
};

export type ChildrenSelectSheetResult = {
  selectedOption: string | number;
};

type Options = Option[];

remapProps(BottomSheetView, {
  className: "style",
});

remapProps(BottomSheet, {
  className: "containerStyle",
});

function ChildrenSelectSheet() {
  const [shouldRender, setShouldRender] = useState(false);
  const { register, close, dismiss, sheet } = useSheet<
    ChildrenSelectSheetPayload,
    ChildrenSelectSheetResult
  >(SheetIds.CHILDREN_SELECT);
  const { payload } = sheet ?? {};
  const options: Options = payload?.options || [];
  const preSelectedOption: string = payload?.preSelectedOption || "";
  const [selectedOption, setSelectedOption] = useState<string | number>(
    preSelectedOption
  );
  const responsiveWidth = useResponsiveWidth();
  const { width } = useWindowDimensions();
  const marginLeft = (width - responsiveWidth) / 2;

  useEffect(() => {
    setShouldRender(true);
  }, []);

  const setBottomSheetRef = useCallback(
    (ref: BottomSheet | null) => {
      if (ref) {
        const refObject = { current: ref };
        register(refObject);
      }
    },
    [register]
  );

  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index === -1) {
        dismiss();
      }
      if (index === 0) {
        setSelectedOption(preSelectedOption);
      }
    },
    [dismiss, preSelectedOption]
  );

  const onClose = useCallback(() => {
    close({
      selectedOption,
    });
  }, [close, selectedOption]);

  const onSelect = useCallback(
    (value: string | number) => {
      close({
        selectedOption: value,
      });
    },
    [close]
  );

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        onPress={onClose}
      />
    ),
    [onClose]
  );

  const renderItem = useCallback(
    ({ item }: { item: Option }) => {
      const onPress = () => {
        setSelectedOption(item.value);
        onSelect(item.value);
      };

      return (
        <View className="mx-5">
          <RadioInput onPress={onPress} disabled={item.disabled}>
            <View className="flex-row items-center gap-2">
              {item.value !== "all" && (
                <Avatar size="sm">
                  <Avatar.Content
                    label={item.label}
                    labelSize="sm"
                    source={item?.imageUrl ? { uri: item.imageUrl } : undefined}
                    size="sm"
                  />
                </Avatar>
              )}
              <RadioInput.Label size="md">{item.label}</RadioInput.Label>
            </View>
            <RadioInput.Icon checked={selectedOption === item.value} />
          </RadioInput>
        </View>
      );
    },
    [onSelect, selectedOption]
  );

  return shouldRender ? (
    <BottomSheet
      ref={setBottomSheetRef}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      index={-1}
      handleComponent={() => <Fragment />}
      onChange={handleSheetChanges}
      backgroundStyle={{
        backgroundColor: "#FCF9F5",
      }}
      style={{ width: responsiveWidth, marginLeft: marginLeft }}
    >
      <BottomSheetFlatList
        data={options}
        renderItem={renderItem}
        ItemSeparatorComponent={() => <View className="h-2" />}
        ListFooterComponent={() => options.length <= 2 ? <View className="pb-safe min-h-[50]" /> : <View className="h-2" />}
        ListHeaderComponent={() => (
          <View className="bg-teachLoop-creamBase rounded-t-[20]">
            <View className="h-6" />
            <View className="mx-5 flex-row">
              <Text className="font-geologica-medium text-black text-xl leading-[1.5] flex-1">
                {payload?.title}
              </Text>
              <Pressable onPress={onClose} className="active:opacity-80">
                <CloseIcon
                  className="size-6"
                  pathClassName="stroke-teachLoop-textPrimary"
                />
              </Pressable>
            </View>
            <View className="h-6" />
          </View>
        )}
        stickyHeaderIndices={[0]}
      />
    </BottomSheet>
  ) : null;
}

export default ChildrenSelectSheet;
